/**
 * Trip Map - Initializes a Mapbox map for trip detail pages.
 * Called from SSE handler with a config JSON object containing
 * all data needed to render the map (token, center, zoom, route, markers).
 */

import { getThemeMapStyle, createHoverMarkerElement, setMapboxToken } from './maps';
import { createWaypointMarker } from './route-detail';
import { COLORS } from './colors';
import { escapeHtml } from './utils';
import type { Waypoint } from './types';

export interface TripMapBounds {
    minLng: number;
    minLat: number;
    maxLng: number;
    maxLat: number;
}

export interface TripMapConfig {
    token: string;
    center: [number, number]; // [lng, lat]
    zoom: number;
    routeGeoJSON?: GeoJSON.Feature;  // GeoJSON Feature for route line
    bounds?: TripMapBounds;      // Route bounds for fitBounds
    startMarker?: [number, number]; // [lng, lat]
    endMarker?: [number, number];   // [lng, lat]
    locationMarker?: {              // For trips without a route
        lngLat: [number, number];
        label: string;
    };
    planRouteURL?: string;          // URL for "Plan Route from Here" button
    embedURL?: string;              // Legacy iframe embed URL
    waypointsJSON?: string;         // JSON string of waypoints data
}

/**
 * Initialize a trip map from a config object.
 * Handles Mapbox GL loading, map creation, route rendering, and markers.
 */
export function initTripMap(config: TripMapConfig): void {
    const section = document.getElementById('map-section');
    const container = document.getElementById('map-container');
    if (!section || !container) return;

    // Hide empty state and show map container
    const emptyState = document.getElementById('map-empty-state');
    if (emptyState) emptyState.style.display = 'none';
    container.style.display = '';

    // Legacy embed URL - just insert an iframe
    if (config.embedURL) {
        const iframe = document.createElement('iframe');
        iframe.src = config.embedURL;
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.frameBorder = '0';
        iframe.scrolling = 'no';
        iframe.allowFullscreen = true;
        iframe.style.minHeight = '400px';
        container.replaceChildren(iframe);
        section.style.display = 'block';
        return;
    }

    section.style.display = 'block';

    // Load Mapbox GL JS if not already loaded
    if (typeof mapboxgl === 'undefined') {
        const link = document.createElement('link');
        link.href = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        const script = document.createElement('script');
        script.src = 'https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js';
        script.onload = () => createMap(config);
        document.head.appendChild(script);
    } else {
        createMap(config);
    }
}

function createMap(config: TripMapConfig): void {
    setMapboxToken(config.token);

    const map = new mapboxgl.Map({
        container: 'map-container',
        style: getThemeMapStyle(),
        center: config.center,
        zoom: config.zoom
    });

    // Static map — disable all interactions
    map.scrollZoom.disable();
    map.boxZoom.disable();
    map.dragRotate.disable();
    map.dragPan.disable();
    map.keyboard.disable();
    map.doubleClickZoom.disable();
    map.touchZoomRotate.disable();

    if (config.routeGeoJSON) {
        map.on('load', () => {
            // Add route line
            map.addSource('route', {
                type: 'geojson',
                data: config.routeGeoJSON as GeoJSON.Feature
            });

            map.addLayer({
                id: 'route',
                type: 'line',
                source: 'route',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': COLORS.routeLine,
                    'line-width': 4,
                    'line-opacity': 0.8
                }
            });

            // Fit bounds
            if (config.bounds) {
                const b = config.bounds;
                map.fitBounds(
                    [[b.minLng, b.minLat], [b.maxLng, b.maxLat]],
                    { padding: { top: 40, bottom: 40, left: 40, right: 40 }, maxZoom: 15 }
                );
            }

            // Start marker
            if (config.startMarker) {
                new mapboxgl.Marker({ color: COLORS.startMarker })
                    .setLngLat(config.startMarker)
                    .setPopup(new mapboxgl.Popup().setHTML('<strong>Start</strong>'))
                    .addTo(map);
            }

            // End marker
            if (config.endMarker) {
                new mapboxgl.Marker({ color: COLORS.endMarker })
                    .setLngLat(config.endMarker)
                    .setPopup(new mapboxgl.Popup().setHTML('<strong>End</strong>'))
                    .addTo(map);
            }

            // Waypoint markers
            if (config.waypointsJSON) {
                try {
                    const waypoints: Waypoint[] = JSON.parse(config.waypointsJSON);
                    waypoints.forEach((wp, idx) => {
                        if (wp.lat && wp.lng) {
                            createWaypointMarker(wp, idx, mapboxgl, map);
                        }
                    });
                } catch (e) {
                    // silent — waypoints are optional
                }
            }

            // Hover marker for elevation chart interaction
            if (config.startMarker) {
                const hoverEl = createHoverMarkerElement(COLORS.hoverMarker);
                window.tripHoverMarker = new mapboxgl.Marker({ element: hoverEl })
                    .setLngLat(config.startMarker)
                    .addTo(map);
                window.tripHoverMarker.getElement().style.display = 'none';
            }
        });
    } else if (config.locationMarker) {
        // Just a marker for the location (no route)
        new mapboxgl.Marker({ color: COLORS.startMarker })
            .setLngLat(config.locationMarker.lngLat)
            .setPopup(new mapboxgl.Popup().setHTML(`<strong>${escapeHtml(config.locationMarker.label)}</strong>`))
            .addTo(map);

        // Add "Plan Route from Here" button
        if (config.planRouteURL) {
            const planRouteDiv = document.createElement('div');
            planRouteDiv.className = 'mt-4 text-center';
            const link = document.createElement('a');
            link.href = config.planRouteURL;
            link.className = 'btn bg-emerald hover:bg-emerald/80 text-white border-none rounded-full px-6';
            link.innerHTML = '<i class="ph-bold ph-path mr-2"></i>Plan Route from Here';
            planRouteDiv.appendChild(link);
            const parent = document.getElementById('map-container')?.parentElement;
            if (parent) parent.appendChild(planRouteDiv);
        }
    }

    // Store waypoints data if provided
    if (config.waypointsJSON) {
        const waypointsEl = document.getElementById('trip-waypoints-data');
        if (waypointsEl) {
            waypointsEl.textContent = config.waypointsJSON;
        }
    }
}
